// datoric-web entry — tiny hash router, no framework shell.
// Routes: #/login, #/auth?token=… (from the email), #/ (pool | contributor
// cabinet by session role), #/contributor/:id (card), #/manage (manager only).
import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { api, clearToken, getToken, type DatoricMeRes as Me } from './api';
import { LoginPage } from './pages/LoginPage';
import { PoolPage } from './pages/PoolPage';
import { ContributorPage } from './pages/ContributorPage';
import { ManagePage } from './pages/ManagePage';
import { MySpacePage } from './pages/MySpacePage';
import './styles.css';

function useHash(): string {
  const [hash, setHash] = useState(location.hash || '#/');
  useEffect(() => {
    const on = () => setHash(location.hash || '#/');
    addEventListener('hashchange', on);
    return () => removeEventListener('hashchange', on);
  }, []);
  return hash;
}

function App() {
  const hash = useHash();
  const [me, setMe] = useState<Me | null | 'loading'>('loading');

  useEffect(() => {
    if (!getToken()) {
      setMe(null);
      return;
    }
    api.me().then(setMe).catch(() => setMe(null));
  }, []);

  // Email link lands here regardless of session state.
  if (hash.startsWith('#/auth')) {
    const token = new URLSearchParams(hash.split('?')[1] ?? '').get('token') ?? undefined;
    return <LoginPage authToken={token} />;
  }

  const contributorId = hash.startsWith('#/contributor/')
    ? decodeURIComponent(hash.slice('#/contributor/'.length))
    : null;

  if (me === 'loading') return <div class="empty">Загружаем…</div>;
  if (me === null || hash.startsWith('#/login')) return <LoginPage />;

  // Contributor session → their own cabinet, nothing else. The worker knows
  // no staff endpoints for this token anyway.
  if (me.role === 'contributor') {
    return (
      <div class="shell">
        <div class="topbar">
          <div class="brand">Datoric · мой кабинет</div>
          <div class="meta">
            <span>{me.name ?? me.email}</span>
            <button
              class="link"
              onClick={() => {
                clearToken();
                location.hash = '#/login';
                location.reload();
              }}
            >
              Выйти
            </button>
          </div>
        </div>
        <MySpacePage />
      </div>
    );
  }

  // The worker gates every /manage endpoint too — this only decides what to
  // render, not who may call what.
  const isManage = hash.startsWith('#/manage') && me.role === 'manager';

  return (
    <div class="shell">
      <div class="topbar">
        <div class="brand">Datoric · дашборд</div>
        {me.role === 'manager' && (
          <nav class="tabs">
            <a href="#/" class={`tab ${!isManage && !contributorId ? 'on' : ''}`}>Пул</a>
            <a href="#/manage" class={`tab ${isManage ? 'on' : ''}`}>Управление</a>
          </nav>
        )}
        <div class="meta">
          <span>
            {me.name} · {me.role === 'manager' ? 'менеджер' : me.role === 'admin' ? 'админ' : 'просмотр'}
          </span>
          <button
            class="link"
            onClick={() => {
              clearToken();
              location.hash = '#/login';
              location.reload();
            }}
          >
            Выйти
          </button>
        </div>
      </div>
      {contributorId ? (
        <ContributorPage me={me} id={contributorId} />
      ) : isManage ? (
        <ManagePage me={me} />
      ) : (
        <PoolPage me={me} />
      )}
    </div>
  );
}

render(<App />, document.getElementById('app')!);
